import { useEffect, useState } from 'react';

export const useForm = ({
  initialValues = {},
  inputs = [],
  submitNewQuestion,
  setUserQuestionsMessage,
  statusMessage,
  isEditingQuestion = false,
  setSelectedQuestion,
  selectedQuestion,
  editSubmitQuestion,
  handleResetQuestionToEdit,
}) => {
  const [formValue, setFormValue] = useState(initialValues);
  const [formInputs, setFormInputs] = useState(inputs);
  const [inputErrorMessage, setInputErrorMessage] = useState({
    text: '',
    correctAnswer: '',
  });

  useEffect(() => {
    if (statusMessage.message && statusMessage.type === 'success') {
      setTimeout(() => {
        setUserQuestionsMessage({
          type: '',
          message: '',
        });
      }, 4000);
    }
  }, [statusMessage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormValue({
      ...formValue,
      [name]: value,
    });
  };

  const handleAddInput = () => {
    const canAddMoreIncorrectAnswers = formInputs.length < 6;
    const newInputNumber = formInputs.length - 2;
    if (
      canAddMoreIncorrectAnswers &&
      formValue[`incorrectAnswer${newInputNumber - 1}`] !== ''
    ) {
      const newInput = {
        name: `incorrectAnswer${newInputNumber}`,
        id: `incorrect-answer${newInputNumber}`,
        type: 'text',
        label: `Incorrect Answer ${newInputNumber} (optional)`,
        placeholder: 'Example: Appendix C',
        hasAddButton: newInputNumber !== 3,
      };

      const indexOfIncorrectAnswer = formInputs.findIndex(
        (input) => input.name === 'incorrectAnswer1',
      );

      const newFormInputs = [...formInputs];
      newFormInputs.splice(
        indexOfIncorrectAnswer + (newInputNumber - 1),
        0,
        newInput,
      );

      const newFormInputsWithUpdatedButtons = newFormInputs.map((input) => {
        if (
          input.name.includes('incorrectAnswer') &&
          input.name !== newInput.name
        ) {
          return {
            ...input,
            hasAddButton: false,
          };
        }
        return input;
      });

      setFormValue({
        ...formValue,
        [newInput.name]: '',
      });

      setFormInputs(newFormInputsWithUpdatedButtons);
    }
  };

  const handleResetForm = () => {
    setFormValue(initialValues);
  };

  const handleResetInputErrors = () => {
    setInputErrorMessage({
      text: '',
      correctAnswer: '',
    });
  };

  const handleResetInputs = () => {
    setFormInputs(inputs);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setUserQuestionsMessage({
      type: '',
      message: '',
    });

    const {
      text,
      correctAnswer,
      incorrectAnswer1 = '',
      incorrectAnswer2 = '',
      incorrectAnswer3 = '',
      incorrectAnswer4 = '',
      answerLocation,
    } = formValue;

    const valuesToSubmit = {
      text,
      correctAnswer,
      incorrectAnswers: [
        incorrectAnswer1,
        incorrectAnswer2,
        incorrectAnswer3,
        incorrectAnswer4,
      ].filter((answer) => answer !== ''),
      notes: answerLocation,
    };

    const valuesToCheck = {
      text,
      correctAnswer,
    };

    if (!text) {
      setInputErrorMessage((iem) => ({
        ...iem,
        text: 'Please enter a correct question.',
      }));
    } else {
      setInputErrorMessage((iem) => ({
        ...iem,
        text: '',
      }));
    }

    if (!correctAnswer) {
      setInputErrorMessage((iem) => ({
        ...iem,
        correctAnswer: 'Please enter a correct answer.',
      }));
    } else {
      setInputErrorMessage((iem) => ({
        ...iem,
        correctAnswer: '',
      }));
    }

    if (Object.values(formValue).some((value) => value.length > 250)) {
      setUserQuestionsMessage({
        type: 'error',
        message: 'Please limit your inputs to 250 characters.',
      });
      return;
    }

    const hasEmptyValues = Object.values(valuesToCheck).some(
      (value) => value === '',
    );

    if (!hasEmptyValues && !isEditingQuestion) {
      submitNewQuestion(valuesToSubmit);
      handleResetForm();
      handleResetInputErrors();
      handleResetInputs();
    }

    if (!hasEmptyValues && isEditingQuestion) {
      const valuesToSubmitEdit = {
        notes: valuesToSubmit.notes,
        text: valuesToSubmit.text,
        correctAnswer: {
          text: valuesToSubmit.correctAnswer,
          id: selectedQuestion.answer.filter((answer) => answer.correct)[0]?.id,
        },
        incorrectAnswers: valuesToSubmit.incorrectAnswers.map((ans, index) => {
          if (
            selectedQuestion.answer.filter((answer) => !answer.correct)[index]
          ) {
            return {
              text: ans,
              id: selectedQuestion.answer.filter((answer) => !answer.correct)[
                index
              ].id,
            };
          }
          return {
            text: ans,
          };
        }),
      };
      editSubmitQuestion(
        valuesToSubmitEdit,
        selectedQuestion.id,
        selectedQuestion.updatedAt,
      );
      handleResetForm();
      handleResetInputErrors();
      handleResetQuestionToEdit();
      handleResetInputs();
      setSelectedQuestion({});
    }
  };

  return {
    formValue,
    formInputs,
    inputErrorMessage,
    handleInputChange,
    handleAddInput,
    handleResetForm,
    handleResetInputErrors,
    handleSubmit,
    setFormValue,
    setFormInputs,
  };
};
